import {
  BarElement,
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import { useEffect, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import DataTable, { TableColumn } from "react-data-table-component";
import { useNavigate } from "react-router";
import ApplicationsFilter, {
  ApplicationFilterState,
} from "../../components/ApplicationsFilter/ApplicationsFilter";
import { Dialog } from "../../components/CommonComponents/Dialog/Dialog";
import { PaginatorForRDT } from "../../components/CommonComponents/Paginator/Paginator";
import SortIcon from "../../components/CommonComponents/SortIcon/SortIcon";
import { tableCustomStyles } from "../../components/CommonComponents/Table/Table";
import { isLevel2 } from "../../hoc/isLevel2";
import { getApplicationTypeColumnLabel } from "../../config/phaseConstants";
import { useToggle } from "../../hooks/common";
import { TApplication } from "../../hooks/level-1-application-approval.hooks";
import {
  useApplicationCountForLevel2,
  useApplicationsForLevel2,
  useApplicationStatsForLevel2,
} from "../../hooks/level-2-application-approval.hooks";
import { formatDate } from "../../utils/date";
import { statusesApplicableForPaymentScreen } from "./LevelTwo.constants";
import { ViewButton } from "../../components/CommonComponents/EditComponent/EditComponent";
import BoxComponent from "../../components/CommonComponents/BoxComponent/BoxComponent";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const options = {
  responsive: true,
  maintainAspectRatio: false,

  plugins: {
    legend: { display: false },
    title: {
      display: true,
      padding: 32,
      font: { size: 24 },
      text: "Applications",
    },
  },
  scales: {
    x: {
      title: {
        display: true,
        text: "Application Status",
        font: { size: 20 },
        padding: 16,
      },
    },
    y: {
      title: {
        display: true,
        text: "Number of Applications",
        font: { size: 20 },
        padding: 16,
      },
      ticks: {
        stepSize: 1,
      },
    },
  },
};

const LevelTwoDashboard = isLevel2(() => {
  const navigate = useNavigate();
  const [filterOpen, toggleFilter] = useToggle();
  const [view, setView] = useState<"chart" | "table">("chart");
  const [page, setPage] = useState(1);

  const [{ sortKey, sortOrder }, setSort] = useState({
    sortKey: "__default__",
    sortOrder: -1,
  });

  const [filterState, setFilterState] = useState<ApplicationFilterState>({
    connector: "and",
    conditions: [],
  });
  const { data } = useApplicationStatsForLevel2({ filters: filterState });
  const labels = (data?.data || []).map((e) => e.status);

  const { isLoading: isLoading1, data: counts } = useApplicationCountForLevel2({
    filters: filterState,
  });
  const { isLoading: isLoading2, data: listData } = useApplicationsForLevel2({
    page,
    limit: 10,
    sort: sortKey,
    sortVal: sortOrder,
    filters: filterState,
  });

  const sortedListData = listData?.data || [];

  useEffect(() => {
    if (listData?.data) {
      console.table(
        listData.data.map(a => ({
          appId: a.applicationId,
          status: a.status,
          queue: a.level2QueueAt,
          isActionAllowed: a.isActionAllowed
        }))
      );
    }
  }, [listData]);

  const chartData: ChartData<"bar", number[], string> = {
    labels,
    datasets: [
      {
        data: (data?.data || []).map((e) => e.count),
        barThickness: 45,
        backgroundColor: "rgba(53, 162, 235,1)",
      },
    ],
  };

  const columns = useMemo(
    (): TableColumn<TApplication & { serviceName?: string }>[] => [
      {
        name: "Application ID",
        sortable: true,
        width: "180px",
        sortField: "applicationId",
        selector: (d) => d.applicationId,
        center: true,
        grow: 0.5,
        cell: (e) => {
          const disabled = !e.isActionAllowed;

          return (
            <span
              className={`font-semibold ${disabled
                ? "text-gray-400 cursor-not-allowed"
                : "text-[#E9B925] cursor-pointer underline"
                }`}
              onClick={() => {
                if (disabled) return;

                if (statusesApplicableForPaymentScreen.includes(e.status)) {
                  navigate(`/level-2-payment-details`, { state: { id: e.id } });
                } else {
                  navigate(`/level-2-application-details`, {
                    state: { id: e.id },
                  });
                }
              }}
            >
              {e.applicationId}
            </span>
          );
        },
      },
      {
        name: "Plot Owner's Name",
        sortable: true,
        sortField: "plot_owner_s_name",
        selector: (d) => d.plot_owner_s_name || "-",
        cell: (d) => (
          <span className="text-center">{d.plot_owner_s_name || "-"}</span>
        ),
        center: true,
        grow: 2,
      },
      {
        name: "Name of Service",
        width: "240px",
        sortField: "name_of_service",
        sortable: false,
        selector: (d) => d.serviceName || "-",
        cell: (d) => <span className="text-center">{d.serviceName}</span>,
        center: true,
        grow: 2,
      },
      {
        name: "Application Type",
        selector: (d) => getApplicationTypeColumnLabel(d),
        center: true,
        width: "180px",
      },
      {
        name: "Status",
        width: "220px",
        center: true,
        sortable: true,
        sortField: "status",
        cell: (d) => <span className="text-center">{d.status}</span>,
      },
      {
        name: "Date of Submission",
        width: "220px",
        center: true,
        sortable: true,
        sortField: "submittedAt",
        cell: (d) => (d.submittedAt ? formatDate(d.submittedAt) : "-"),
      },
      {
        name: 'Created By',
        selector: (row: any) => row.createdBy,
        cell: (row: any) => <span className="text-center">{row.createdBy}</span>,
        center: true,
        sortField: "createdBy",
        sortable: true,
        width: "170px",
      },
      {
        name: 'Created On',
        selector: (row: any) => formatDate(new Date(row.createdAt)),
        center: true,
        sortField: "createdAt",
        sortable: true,
        width: "170px",
      },
      {
        name: 'Modified By',
        selector: (row: any) => row.modifiedBy,
        cell: (row: any) => <span className="text-center">{row.modifiedBy}</span>,
        center: true,
        sortable: true,
        width: "170px",
      },
      {
        name: 'Modified On',
        selector: (row: any) => formatDate(new Date(row.updatedAt)),
        center: true,
        sortable: true,
        width: "170px",
      },
      {
        name: "Action",
        center: true,
        grow: 0.5,
        cell: (e) => {
          const disabled = !e.isActionAllowed;

          return (
            <div className="flex justify-center">
              <ViewButton
                disabled={disabled}
                onClick={() => {
                  if (disabled) return;

                  if (statusesApplicableForPaymentScreen.includes(e.status)) {
                    navigate(`/level-2-payment-details`, { state: { id: e.id } });
                  } else {
                    navigate(`/level-2-application-details`, {
                      state: { id: e.id },
                    });
                  }
                }}
              />
            </div>
          );
        }

      },
    ],
    [navigate]
  );

  return (
    <>
      <div className="p-4">
        <h4 className="text-xl sm:text-2xl font-bold pb-4">Dashboard</h4>
        <BoxComponent>
          <div className="p-2 sm:p-4 md:p-4 md:pt-2 bg-white">
            <div className="flex justify-end mb-4">
              {/* <h4 className="text-xl sm:text-2xl font-bold">Dashboard</h4> */}
              <div className="flex space-x-4">
                <button
                  className="p-3 bg-white border border-[#968f8f] rounded-xl flex justify-center items-center"
                  onClick={() => {
                    setView((e) => (e === "chart" ? "table" : "chart"));
                    setPage(1);
                  }}
                >
                  {view === "table" ? (
                    <i className="fa fa-bar-chart text-primary m-0" />
                  ) : (
                    <i className="fa fa-list-ul text-primary m-0" />
                  )}
                </button>
                <button
                  className="p-3 bg-white border border-[#968f8f] rounded-xl flex justify-center items-center min-w-[42px]"
                  onClick={toggleFilter}
                >
                  <i className="fa fa-filter text-primary m-0" />
                </button>
              </div>
            </div>
            {view === "chart" ? (
              <div
                className="chart-container overflow-x-auto"
                style={{ position: "relative", height: "75vh", width: "100%" }}
              >
                <div style={{ minWidth: "900px", height: "100%" }}>
                  <Bar options={options} data={chartData} />
                </div>
              </div>
            ) : (
              <DataTable
                pagination
                sortServer
                paginationServer
                className="mt-4"
                columns={columns}
                sortIcon={<SortIcon />}
                data={sortedListData}
                customStyles={tableCustomStyles}
                onChangePage={(p) => setPage(p)}
                paginationTotalRows={counts?.data || 0}
                progressPending={isLoading1 || isLoading2}
                paginationComponent={PaginatorForRDT}
                onSort={(col, dir) => {
                  setSort({
                    sortKey: col.sortField || "",
                    sortOrder: dir === "asc" ? 1 : -1,
                  });
                }}
              />
            )}
          </div>
        </BoxComponent>
      </div>
      <Dialog
        onClose={toggleFilter}
        open={filterOpen}
        className="flex flex-col sm:w-[600px] lg:w-[800px]"
      >
        <ApplicationsFilter
          onClose={toggleFilter}
          defaultFilter={filterState}
          onChange={setFilterState}
        />
      </Dialog>
    </>
  );
});

export default LevelTwoDashboard;
